import React, { createContext, useCallback, useContext, useMemo, useState, useSyncExternalStore } from 'react';
import { faCheck, faCopy } from '@fortawesome/pro-regular-svg-icons';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { Button } from '@/modules/Button';
import { cn } from '@/shared/utils/cn';
import {
  dracula,
  tomorrow,
  oneDark,
  oneLight,
  atomDark,
  materialDark,
  materialLight,
  prism,
  dark,
} from 'react-syntax-highlighter/dist/esm/styles/prism';

/** Tema Orbit efectivo para hijos (`auto` en código): forzado por `StoryBox` o el `data-theme` de `documentElement`. */
type StoryBoxOrbitAppearance = 'light' | 'dark';

const StoryBoxForcedAppearanceContext = createContext<StoryBoxOrbitAppearance | null>(null);

function getDocumentThemeSnapshot(): StoryBoxOrbitAppearance {
  if (typeof document === 'undefined') return 'light';
  return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
}

function subscribeDocumentTheme(onStoreChange: () => void): () => void {
  if (typeof document === 'undefined') return () => {};
  const el = document.documentElement;
  const mo = new MutationObserver(onStoreChange);
  mo.observe(el, { attributes: true, attributeFilter: ['data-theme'] });
  return () => mo.disconnect();
}

function useEffectiveOrbitAppearance(): StoryBoxOrbitAppearance {
  const forced = useContext(StoryBoxForcedAppearanceContext);
  const docTheme = useSyncExternalStore(
    subscribeDocumentTheme,
    getDocumentThemeSnapshot,
    getDocumentThemeSnapshot,
  );
  return forced ?? docTheme;
}

interface StoryBoxProps {
  children: React.ReactNode;
  className?: string;
  fullHeight?: boolean;
  /**
   * Alinea tokens Orbit (`ui-text-*`, `ui-bg-*`) y el modo **`auto`** de `StoryBox.Code` con claro u oscuro.
   * - **`inherit`**: no fija `data-theme` en el contenedor (sigue el ancestro, p. ej. `<html>` en Storybook).
   * - **`light` | `dark`**: aplica `data-theme` en la raíz del StoryBox (útil en canvas o docs sin cambiar el tema global).
   */
  appearance?: 'inherit' | StoryBoxOrbitAppearance;
}

interface StoryBoxTitleProps {
  children: React.ReactNode;
}

interface StoryBoxDescriptionProps {
  children: React.ReactNode;
}

interface StoryBoxContentProps {
  children: React.ReactNode;
  className?: string;
}

interface StoryBoxCodeProps {
  /**
   * Preferido: **string** (fragmento TSX curado; alineado a Docs). `ReactNode` es legado: se serializa con heurística **no fiable** solo para no romper historias existentes; migrar a string. Ver `imj-ui-obligations-storybook-contract`.
   */
  children: string | React.ReactNode;
  language?: string;
  showLineNumbers?: boolean;
  customStyle?: React.CSSProperties;
  /**
   * Resaltado de sintaxis. **`auto`** (por defecto) elige un tema claro u oscuro según Orbit:
   * `appearance` de `StoryBox` si está fijado, si no el `data-theme` de `documentElement` (p. ej. toolbar Orbit en Storybook).
   */
  theme?:
    | 'auto'
    | 'dracula'
    | 'tomorrow'
    | 'oneDark'
    | 'oneLight'
    | 'atomDark'
    | 'materialDark'
    | 'materialLight'
    | 'prism'
    | 'dark';
}

// Mapa de temas disponibles
const themes = {
  dracula,
  tomorrow,
  oneDark,
  oneLight,
  atomDark,
  materialDark,
  materialLight,
  prism,
  dark,
};

/** @internal Solo compatibilidad con historias que aún pasan JSX; no usar como documentación fiable. */
const jsxToString = (jsx: React.ReactNode): string => {
  if (typeof jsx === 'string') return jsx;

  if (React.isValidElement(jsx)) {
    const element = jsx as React.ReactElement;
    const { type, props } = element;

    const componentName = typeof type === 'string' ? type : type.name || 'Component';

    const propsString = Object.entries(props)
      .filter(([key]) => key !== 'children')
      .map(([key, value]) => {
        if (typeof value === 'string') {
          return `${key}="${value}"`;
        } else if (typeof value === 'boolean') {
          return value ? key : `${key}={false}`;
        } else if (typeof value === 'function') {
          const functionString = value.toString();
          if (functionString.includes('=>')) {
            return `${key}={${functionString}}`;
          }
          return `${key}={${functionString}}`;
        } else if (value === null) {
          return `${key}={null}`;
        } else if (value === undefined) {
          return `${key}={undefined}`;
        } else if (typeof value === 'object') {
          try {
            if (
              value &&
              typeof value === 'object' &&
              '$$typeof' in value &&
              React.isValidElement(value)
            ) {
              return `${key}={/* React Element */}`;
            }
            return `${key}={${JSON.stringify(value)}}`;
          } catch {
            return `${key}={/* objeto complejo */}`;
          }
        } else {
          return `${key}={${JSON.stringify(value)}}`;
        }
      })
      .join(' ');

    let childrenString = '';
    if (props.children) {
      if (Array.isArray(props.children)) {
        childrenString = props.children
          .filter((child: React.ReactNode): child is React.ReactNode => child !== null)
          .map((child: React.ReactNode) => jsxToString(child))
          .join('\n');
      } else {
        childrenString = jsxToString(props.children);
      }
    }

    const propsPart = propsString ? ` ${propsString}` : '';

    if (!childrenString) {
      return `<${componentName}${propsPart} />`;
    }

    const indentedChildren = childrenString
      .split('\n')
      .map((line) => `  ${line}`)
      .join('\n');
    return `<${componentName}${propsPart}>\n${indentedChildren}\n</${componentName}>`;
  }

  if (Array.isArray(jsx)) {
    return jsx.map((item) => jsxToString(item)).join('\n');
  }

  return String(jsx);
};

const StoryBoxTitle = ({ children }: StoryBoxTitleProps) => (
  <h2 className="ui-text-lg ui-font-semibold ui-text-primary">{children}</h2>
);

const StoryBoxDescription = ({ children }: StoryBoxDescriptionProps) => (
  <div className="ui-text-tertiary ui-text-body-xs-regular">{children}</div>
);

const StoryBoxContent = ({ children, className = '' }: StoryBoxContentProps) => (
  <div className={`ui-flex ui-w-full ui-items-center ui-gap-2 ${className}`}>{children}</div>
);

const COPY_FEEDBACK_MS = 500;

const StoryBoxCode = ({
  children,
  language = 'jsx',
  showLineNumbers = false,
  customStyle = {},
  theme = 'auto',
}: StoryBoxCodeProps) => {
  const codeString = typeof children === 'string' ? children : jsxToString(children);
  const [copied, setCopied] = useState(false);
  const orbitAppearance = useEffectiveOrbitAppearance();

  const resolvedHighlighterTheme = useMemo(() => {
    if (theme !== 'auto') return theme;
    return orbitAppearance === 'dark' ? 'oneDark' : 'prism';
  }, [theme, orbitAppearance]);

  const handleCopy = useCallback(() => {
    const text = codeString;
    void (async () => {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        window.setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
      } catch {
        try {
          const ta = document.createElement('textarea');
          ta.value = text;
          ta.setAttribute('readonly', '');
          ta.style.position = 'fixed';
          ta.style.left = '-9999px';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
          setCopied(true);
          window.setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
        } catch {
          /* noop */
        }
      }
    })();
  }, [codeString]);

  const selectedTheme = themes[resolvedHighlighterTheme] ?? themes.oneDark;

  return (
    <div
      className={cn(
        'ui-relative ui-w-full ui-overflow-hidden ui-rounded-md ui-border ui-border-default ui-bg-surface-secondary',
      )}
    >
      <div className="ui-flex ui-justify-end ui-border-b ui-border-default ui-pb-4 ui-pl-8 ui-pr-8 ui-pt-4">
        <Button
          variant="outlined"
          color="secondary"
          size="xxs"
          leftSlot={copied ? faCheck : faCopy}
          onClick={handleCopy}
          aria-label={copied ? 'Código copiado' : 'Copiar código'}
        >
          {copied ? 'Copiado' : 'Copiar'}
        </Button>
      </div>
      <div className="ui-py-4 ui-pl-8 ui-pr-8">
        <SyntaxHighlighter
          language={language}
          style={selectedTheme}
          showLineNumbers={showLineNumbers}
          customStyle={{
            margin: 0,
            padding: 0,
            borderRadius: 0,
            fontSize: '0.875rem',
            lineHeight: 1.55,
            background: 'transparent',
            display: 'flex',
            ...customStyle,
          }}
          codeTagProps={{
            style: {
              background: 'transparent',
            },
          }}
        >
          {codeString}
        </SyntaxHighlighter>
      </div>
    </div>
  );
};

/**
 * StoryBox - Componente contenedor para documentación y ejemplos
 *
 * Componente que proporciona una estructura consistente para mostrar documentación,
 * ejemplos de código y contenido en Storybook o documentación.
 * Incluye subcomponentes para título, descripción, contenido y código con syntax highlighting y botón **Copiar**.
 *
 * **Tema Orbit:** título y descripción usan colores semánticos (`ui-text-primary`, `ui-text-tertiary`). La prop
 * **`appearance`** puede fijar `data-theme` en el contenedor; **`StoryBox.Code`** con **`theme="auto"`** (por defecto)
 * elige resaltado claro (`prism`) u oscuro (`oneDark`) según ese contexto o el `data-theme` global.
 *
 * @example
 * ```tsx
 * // StoryBox básico
 * <StoryBox>
 *   <StoryBox.Title>Título</StoryBox.Title>
 *   <StoryBox.Description>Descripción del ejemplo</StoryBox.Description>
 *   <StoryBox.Content>
 *     <Button>Ejemplo</Button>
 *   </StoryBox.Content>
 * </StoryBox>
 *
 * // StoryBox con código
 * <StoryBox>
 *   <StoryBox.Title>Ejemplo de código</StoryBox.Title>
 *   <StoryBox.Code language="tsx" theme="auto">
 *     {`<Button onClick={handleClick}>Click me</Button>`}
 *   </StoryBox.Code>
 * </StoryBox>
 * ```
 *
 * @see {@link StoryBox.Title}, {@link StoryBox.Description}, {@link StoryBox.Content}, {@link StoryBox.Code} para subcomponentes
 */
export const StoryBox = ({
  children,
  className = '',
  fullHeight = false,
  appearance = 'inherit',
}: StoryBoxProps) => {
  const forcedAppearance = appearance === 'inherit' ? null : appearance;
  const root = (
    <div
      className={cn(
        'ui-imj',
        'ui-flex ui-flex-col ui-gap-2 ui-rounded-md ui-bg-surface ui-p-16',
        fullHeight && 'ui-h-full',
        className,
      )}
      {...(appearance !== 'inherit' ? { 'data-theme': appearance } : {})}
    >
      {children}
    </div>
  );

  return (
    <StoryBoxForcedAppearanceContext.Provider value={forcedAppearance}>
      {root}
    </StoryBoxForcedAppearanceContext.Provider>
  );
};

// Asignar subcomponentes al componente principal
StoryBox.Title = StoryBoxTitle;
StoryBox.Description = StoryBoxDescription;
StoryBox.Content = StoryBoxContent;
StoryBox.Code = StoryBoxCode;
